// Pure grid builder for the monthly posts view (feature 007-monthly-posts-view).
// No React / Redux: turns a cursor month + flat activity list into a 6-week (Monday-start)
// grid of day cells, each carrying its mapped WeekPostCardModel cards, a 3-card visible slice
// and the overflow roll-up metadata. date-fns only.
import {
    eachDayOfInterval,
    endOfMonth,
    endOfWeek,
    format,
    isSameDay,
    isSameMonth,
    startOfDay,
    startOfMonth,
    startOfWeek,
} from "date-fns";
import {PeerActivityType} from "../../redux/Content/PeerActivity/types";
import {mapActivityToWeekCardModel, WeekPostCardModel} from "./WeekPostCard.helpers";

type Translate = (key: string) => string;

export interface MonthDayCell {
    date: Date;
    dayNumber: number;
    isToday: boolean;
    isPast: boolean; // calendar day earlier than today — no "Schedule" affordance (can't post in the past)
    isWeekend: boolean;
    isOutOfMonth: boolean;
    posts: WeekPostCardModel[];
    visiblePosts: WeekPostCardModel[];
    overflowCount: number;
    overflowHasFailed: boolean;
}

export interface MonthWeek {
    days: MonthDayCell[];
}

export interface MonthGrid {
    cursor: Date;
    weeks: MonthWeek[];
    rangeStart: Date;
    rangeEnd: Date;
}

export interface BuildMonthGridOptions {
    weekStartsOn?: 0 | 1;
    limit?: number;
    t: Translate;
    language: string;
}

const DEFAULT_LIMIT = 3;

// The posting moment used both for bucketing (calendar day) and sort order.
function activityDate(a: PeerActivityType): Date | null {
    const raw = a.activeFrom || a.start;
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
}

// "Failed" is read the same way the card reads its error treatment: the danger status class
// (STATUS_ERROR / STATUS_REJECTED both map to `danger` in PeerActivityStatusClass).
export function isFailedModel(model: WeekPostCardModel): boolean {
    return model.statusClass === "danger";
}

// Droppable ids are the cell date as an ISO calendar day (yyyy-MM-dd, local) — a stable,
// human-readable key the drag handler parses back into a Date for onReschedule (US3).
export function formatDroppableId(date: Date): string {
    return format(date, "yyyy-MM-dd");
}

export function parseDroppableId(id: string): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(id);
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d.getTime()) ? null : d;
}

function chunkWeeks(cells: MonthDayCell[]): MonthWeek[] {
    const weeks: MonthWeek[] = [];
    for (let i = 0; i < cells.length; i += 7) {
        weeks.push({days: cells.slice(i, i + 7)});
    }
    return weeks;
}

export function buildMonthGrid(
    cursor: Date,
    activities: PeerActivityType[],
    opts: BuildMonthGridOptions,
): MonthGrid {
    const weekStartsOn = opts.weekStartsOn ?? 1;
    const limit = opts.limit ?? DEFAULT_LIMIT;
    const {t, language} = opts;

    const rangeStart = startOfWeek(startOfMonth(cursor), {weekStartsOn});
    const rangeEnd = endOfWeek(endOfMonth(cursor), {weekStartsOn});
    const days = eachDayOfInterval({start: rangeStart, end: rangeEnd});
    const now = new Date();
    const todayStart = startOfDay(now);

    // Pre-resolve each activity's posting moment once.
    const dated = (activities || [])
        .map((a) => ({activity: a, at: activityDate(a)}))
        .filter((x): x is {activity: PeerActivityType; at: Date} => x.at != null);

    const cells: MonthDayCell[] = days.map((date) => {
        const dayPosts = dated
            .filter((x) => isSameDay(x.at, date))
            .sort((a, b) => a.at.getTime() - b.at.getTime());

        // `start` is the resolved posting moment (activeFrom||start) so startTime reflects it;
        // mirrors how the week/day boards feed mapActivityToWeekCardModel.
        const posts = dayPosts.map((x) =>
            mapActivityToWeekCardModel(x.activity, x.at, x.activity.end ? new Date(x.activity.end) : null, t, language),
        );

        const visiblePosts = posts.slice(0, limit);
        const hidden = posts.slice(limit);

        return {
            date,
            dayNumber: date.getDate(),
            isToday: isSameDay(date, now),
            isPast: startOfDay(date).getTime() < todayStart.getTime(),
            isWeekend: [0, 6].includes(date.getDay()),
            isOutOfMonth: !isSameMonth(date, cursor),
            posts,
            visiblePosts,
            overflowCount: hidden.length,
            overflowHasFailed: hidden.some(isFailedModel),
        };
    });

    return {cursor, weeks: chunkWeeks(cells), rangeStart, rangeEnd};
}
